import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Save } from 'lucide-react';
import { useUpdateTransactionLimitsConfig } from '../../../hooks/useApi';
import { TransactionLimitsConfig, UpdateTransactionLimitsConfigRequest } from '../../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';

interface ConfigFormProps {
  config: TransactionLimitsConfig;
  onSuccess: () => void;
  // KIOSK-720: platform_admin is read-only/monitoring — inputs disabled, Save hidden.
  canEdit?: boolean;
}

type ConfigFormValues = UpdateTransactionLimitsConfigRequest & {
  gameWallet409GuardSinceInput?: string;
};

function isoToDatetimeLocal(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function datetimeLocalToIso(value?: string | null): string | null {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

const ConfigForm: React.FC<ConfigFormProps> = ({ config, onSuccess, canEdit = true }) => {
  const updateConfig = useUpdateTransactionLimitsConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<ConfigFormValues>({
    defaultValues: {
      transactionLimits: {
        ...config.transactionLimits,
        gameProviderHealthCheck: {
          enabled: config.transactionLimits.gameProviderHealthCheck?.enabled ?? true,
        },
      },
      rateLimits: config.rateLimits,
      fraudFlags: config.fraudFlags,
      gameWallet409GuardSinceInput: isoToDatetimeLocal(config.transactionLimits.gameWallet409GuardEnabledSince),
    },
  });

  const onSubmit = async (data: ConfigFormValues) => {
    setIsSubmitting(true);
    try {
      await updateConfig.mutateAsync({
        transactionLimits: {
          ...config.transactionLimits,
          ...data.transactionLimits,
          gameProviderHealthCheck: {
            ...config.transactionLimits.gameProviderHealthCheck,
            enabled: data.transactionLimits?.gameProviderHealthCheck?.enabled ?? true,
          },
          gameWallet409GuardEnabledSince: datetimeLocalToIso(data.gameWallet409GuardSinceInput),
        },
        rateLimits: data.rateLimits,
        fraudFlags: data.fraudFlags,
      });
      onSuccess();
    } catch (error) {
      // Error is handled by the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <fieldset disabled={!canEdit} className="space-y-6 min-w-0 border-0 p-0 m-0">
      {/* Transaction Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Withdrawal Auto-Approve Threshold (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.withdrawalAutoApproveThreshold', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.transactionLimits?.withdrawalAutoApproveThreshold?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Shop Fronting Debt Threshold (credits)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.shopFrontingDebtThreshold', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Must be a non-negative number' },
                })}
                error={errors.transactionLimits?.shopFrontingDebtThreshold?.message}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Global default. New player online deposits are paused when a shop's outstanding
                fronting debt reaches this value (per-shop overrides win). 0 = any debt pauses deposits.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Max Deposit Per Transaction (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.maxDepositPerTransaction', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.transactionLimits?.maxDepositPerTransaction?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Max Deposit Per Day (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.maxDepositPerDay', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.transactionLimits?.maxDepositPerDay?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Max Withdrawal Per Day (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.maxWithdrawalPerDay', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.transactionLimits?.maxWithdrawalPerDay?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Min Withdrawal Amount (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.minWithdrawalAmount', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.transactionLimits?.minWithdrawalAmount?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Min Wallet Deposit Amount (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.minDepositAmount', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.transactionLimits?.minDepositAmount?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum amount when players add real funds to their wallet (crypto deposit). Not the per-game transfer minimum.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                New Account Limit (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.newAccountLimit', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.transactionLimits?.newAccountLimit?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                New Account Days
              </label>
              <Input
                type="number"
                {...register('transactionLimits.newAccountDays', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1 day' },
                })}
                error={errors.transactionLimits?.newAccountDays?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Account Maturity (Hours)
              </label>
              <Input
                type="number"
                {...register('transactionLimits.maturityHours', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Must be 0 or greater' },
                })}
                error={errors.transactionLimits?.maturityHours?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Hours a new account must wait before withdrawing.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Maturity Grandfather Date
              </label>
              <Input
                type="date"
                {...register('transactionLimits.maturityGrandfatherDate')}
                error={errors.transactionLimits?.maturityGrandfatherDate?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Users created before this date are exempt.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Game Wallet Guards &amp; Auto-Refund</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 rounded-lg border p-3">
            <input
              type="checkbox"
              className="h-4 w-4"
              {...register('transactionLimits.gameProviderHealthCheck.enabled')}
            />
            <span className="text-sm font-medium">
              Enable pre-transaction game provider health check
            </span>
          </label>
          <p className="text-xs text-muted-foreground">
            When enabled, the system verifies game provider API availability before auto game
            deposits, withdrawals, and balance sync. Manual games are always skipped. Turn off only
            when provider outages should not block player transactions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="game-wallet-409-guard-since">
                409 Guard Enabled Since
              </label>
              <Input
                id="game-wallet-409-guard-since"
                type="datetime-local"
                {...register('gameWallet409GuardSinceInput')}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to disable the cutoff. When set, only transactions with created_at on or
                after this moment can trigger DEPOSIT_IN_PROGRESS or GAME_WALLET_RECOVERY_PENDING.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Auto-Refund Warning Threshold (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('transactionLimits.autoRefundWarningAmountThreshold', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0.01, message: 'Must be greater than 0' },
                })}
                error={errors.transactionLimits?.autoRefundWarningAmountThreshold?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Cumulative ambiguous deposit auto-refund amount that triggers a player withdrawal
                warning/block. Clear per player from User detail when reviewed.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rate Limits */}
      <Card>
        <CardHeader>
          <CardTitle>Rate Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Max Deposits Per Hour
              </label>
              <Input
                type="number"
                {...register('rateLimits.maxDepositsPerHour', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1' },
                })}
                error={errors.rateLimits?.maxDepositsPerHour?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Max Withdrawals Per Hour
              </label>
              <Input
                type="number"
                {...register('rateLimits.maxWithdrawalsPerHour', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1' },
                })}
                error={errors.rateLimits?.maxWithdrawalsPerHour?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Max Game Transfers Per Hour
              </label>
              <Input
                type="number"
                {...register('rateLimits.maxGameTransfersPerHour', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1' },
                })}
                error={errors.rateLimits?.maxGameTransfersPerHour?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Min Time Between Withdrawals (Minutes)
              </label>
              <Input
                type="number"
                {...register('rateLimits.minTimeBetweenWithdrawalsMinutes', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be 0 or greater' },
                })}
                error={errors.rateLimits?.minTimeBetweenWithdrawalsMinutes?.message}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fraud Flags */}
      <Card>
        <CardHeader>
          <CardTitle>Fraud Detection Flags</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                High Deposit Volume Threshold (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('fraudFlags.highDepositVolumeThreshold', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.fraudFlags?.highDepositVolumeThreshold?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                High Deposit Volume Time Window (Hours)
              </label>
              <Input
                type="number"
                step="0.1"
                {...register('fraudFlags.highDepositVolumeTimeWindowHours', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0.1, message: 'Must be greater than 0' },
                })}
                error={errors.fraudFlags?.highDepositVolumeTimeWindowHours?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Rapid Deposit-Withdrawal Time Window (Minutes)
              </label>
              <Input
                type="number"
                {...register('fraudFlags.rapidDepositWithdrawalTimeWindowMinutes', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1 minute' },
                })}
                error={errors.fraudFlags?.rapidDepositWithdrawalTimeWindowMinutes?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                High Deposit Frequency Count
              </label>
              <Input
                type="number"
                {...register('fraudFlags.highDepositFrequencyCount', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1' },
                })}
                error={errors.fraudFlags?.highDepositFrequencyCount?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                High Deposit Frequency Time Window (Minutes)
              </label>
              <Input
                type="number"
                {...register('fraudFlags.highDepositFrequencyTimeWindowMinutes', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1 minute' },
                })}
                error={errors.fraudFlags?.highDepositFrequencyTimeWindowMinutes?.message}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Abnormal Profit Detection */}
      <Card>
        <CardHeader>
          <CardTitle>Abnormal Profit Detection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Configure thresholds for detecting abnormal withdrawal profit ratios. Withdrawals exceeding these thresholds will be flagged for admin review.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Profit Ratio Threshold
              </label>
              <Input
                type="number"
                step="0.1"
                {...register('fraudFlags.profitRatioThreshold', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1' },
                })}
                error={errors.fraudFlags?.profitRatioThreshold?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Max withdrawal/deposit ratio before flagging. E.g. 3.0 means deposit $10, withdraw up to $30.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Absolute Win Threshold (USD)
              </label>
              <Input
                type="number"
                step="1"
                {...register('fraudFlags.absoluteWinThreshold', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.fraudFlags?.absoluteWinThreshold?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Net profit above this amount always triggers a flag, regardless of ratio.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                New Account Profit Ratio
              </label>
              <Input
                type="number"
                step="0.1"
                {...register('fraudFlags.newAccountProfitRatio', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1' },
                })}
                error={errors.fraudFlags?.newAccountProfitRatio?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Stricter profit ratio threshold for new accounts.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                New Account Period (Days)
              </label>
              <Input
                type="number"
                {...register('fraudFlags.newAccountPeriodDays', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1 day' },
                })}
                error={errors.fraudFlags?.newAccountPeriodDays?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Number of days an account is considered "new" (stricter ratio applies).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Lookback Period (Days)
              </label>
              <Input
                type="number"
                {...register('fraudFlags.lookbackPeriodDays', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 1, message: 'Must be at least 1 day' },
                })}
                error={errors.fraudFlags?.lookbackPeriodDays?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Rolling window (days) for analyzing deposits and withdrawals.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Min Deposit for Analysis (USD)
              </label>
              <Input
                type="number"
                step="0.01"
                {...register('fraudFlags.minDepositForAnalysis', {
                  valueAsNumber: true,
                  required: 'This field is required',
                  min: { value: 0, message: 'Must be a positive number' },
                })}
                error={errors.fraudFlags?.minDepositForAnalysis?.message}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum total deposit to trigger analysis (avoids false positives on very small deposits).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      </fieldset>

      {/* Submit Button */}
      {canEdit && (
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || updateConfig.isLoading}>
            {isSubmitting || updateConfig.isLoading ? (
              <>
                <LoadingSpinner className="h-4 w-4 mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      )}
    </form>
  );
};

export default ConfigForm;

